import { useState, useEffect, useCallback } from 'react'
import { Loader2, CheckCircle2, AlertTriangle, BookOpen, ArrowRight, LogIn } from 'lucide-react'
import clsx from 'clsx'

/**
 * Utilitário de OneNote para o usuário final — página independente, fora do console de migração.
 *
 * Existe porque a API de OneNote recusa token de aplicação desde 31/03/2025 e o token delegado alcança
 * só o conteúdo do próprio usuário: nem o administrador enxerga bloco alheio. Não há caminho automático;
 * a pessoa precisa entrar com as duas contas. A tela existe pra tornar isso simples.
 *
 * Sem login do produto: quem usa é o funcionário do cliente. A autenticação que vale é a da Microsoft.
 */

interface Estado {
  origemConectada: boolean
  origemUpn: string | null
  destinoConectada: boolean
  destinoUpn: string | null
  aguardandoLogin: boolean
  userCode: string | null
  verificationUrl: string | null
  fase: string
  paginasTotal: number
  paginasFeitas: number
  paginasPuladas: number
  paginasFalhas: number
  blocoAtual: string | null
  avisos: string[]
  error: string | null
}

interface Bloco { id: string; nome: string; secoes: number; paginas: number }

const API = '/api/onenote-app'

/** Id de sessão do navegador: identifica a sessão no servidor, onde os tokens ficam. */
function obterSessionId(): string {
  const chave = 'onenote-app-sessao'
  let id = localStorage.getItem(chave)
  if (!id) {
    id = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('')
    localStorage.setItem(chave, id)
  }
  return id
}

export function OneNoteAppPage() {
  const [sessionId] = useState(obterSessionId)
  const [estado, setEstado] = useState<Estado | null>(null)
  const [blocos, setBlocos] = useState<Bloco[] | null>(null)
  const [escolhidos, setEscolhidos] = useState<Set<string>>(new Set())
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const buscarEstado = useCallback(async () => {
    try {
      const r = await fetch(`${API}/sessoes/${sessionId}`)
      if (r.ok) setEstado(await r.json())
    } catch { /* rede instável não deve quebrar a tela */ }
  }, [sessionId])

  useEffect(() => {
    buscarEstado()
    // Rápido enquanto há login pendente ou transferência em curso; devagar no resto.
    const ritmo = estado?.aguardandoLogin || estado?.fase === 'Transferindo' ? 2000 : 8000
    const t = setInterval(buscarEstado, ritmo)
    return () => clearInterval(t)
  }, [buscarEstado, estado?.aguardandoLogin, estado?.fase])

  async function entrar(lado: 'origem' | 'destino') {
    setErro(null)
    try {
      const r = await fetch(`${API}/sessoes/${sessionId}/login/${lado}`, { method: 'POST' })
      const d = await r.json()
      if (!d.started) setErro(d.error || 'Não foi possível iniciar o login.')
      buscarEstado()
    } catch { setErro('Falha de rede ao iniciar o login.') }
  }

  async function listar() {
    setCarregando(true); setErro(null)
    try {
      const r = await fetch(`${API}/sessoes/${sessionId}/blocos`)
      const d = await r.json()
      if (!r.ok) { setErro(d.error || 'Não foi possível listar os blocos.'); return }
      setBlocos(d)
      setEscolhidos(new Set(d.map((b: Bloco) => b.id)))
    } catch { setErro('Falha de rede ao listar os blocos.') }
    finally { setCarregando(false) }
  }

  async function transferir() {
    setErro(null)
    try {
      const r = await fetch(`${API}/sessoes/${sessionId}/transferir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notebookIds: [...escolhidos] }),
      })
      const d = await r.json()
      if (!r.ok) setErro(d.error || 'Não foi possível iniciar a transferência.')
      buscarEstado()
    } catch { setErro('Falha de rede ao iniciar a transferência.') }
  }

  const e = estado
  const transferindo = e?.fase === 'Transferindo'
  const concluido = e?.fase === 'Concluido'
  const prontoPraTransferir = e?.origemConectada && e?.destinoConectada && (escolhidos.size > 0)

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="h-7 w-7 text-brand-600" />
          <h1 className="text-2xl font-bold">Transferir anotações do OneNote</h1>
        </div>
        <p className="text-sm text-slate-600 mb-8">
          Leva seus blocos de anotações da conta antiga para a conta nova. Você entra com as duas contas,
          escolhe o que levar, e o resto é automático.
        </p>

        {/* Código do device code: aparece assim que um login é iniciado. */}
        {e?.aguardandoLogin && e.userCode && (
          <div className="mb-6 p-4 bg-brand-50 border border-brand-200 rounded-lg">
            <p className="text-sm mb-2">Abra o endereço abaixo e informe o código:</p>
            <a href={e.verificationUrl || 'https://login.microsoft.com/device'} target="_blank"
               rel="noreferrer" className="text-brand-700 underline text-sm">
              {e.verificationUrl}
            </a>
            <div className="mt-3 text-3xl font-mono font-bold tracking-widest">{e.userCode}</div>
            <p className="text-xs text-slate-600 mt-2 flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> Aguardando você entrar...
            </p>
          </div>
        )}

        {/* Passo 1 e 2: as duas contas */}
        <div className="space-y-3 mb-6">
          <Passo
            numero={1}
            titulo="Conta antiga"
            descricao="Onde estão as anotações hoje."
            conectado={!!e?.origemConectada}
            upn={e?.origemUpn ?? null}
            desabilitado={!!e?.aguardandoLogin || transferindo}
            onEntrar={() => entrar('origem')}
          />
          <Passo
            numero={2}
            titulo="Conta nova"
            descricao="Para onde as anotações vão."
            conectado={!!e?.destinoConectada}
            upn={e?.destinoUpn ?? null}
            desabilitado={!!e?.aguardandoLogin || transferindo}
            onEntrar={() => entrar('destino')}
          />
        </div>

        {/* Passo 3: escolher blocos */}
        {e?.origemConectada && !transferindo && !concluido && (
          <div className="mb-6 bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">3. Escolha os blocos</h2>
              <button onClick={listar} disabled={carregando}
                className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50">
                {carregando ? 'Buscando...' : blocos ? 'Atualizar lista' : 'Buscar meus blocos'}
              </button>
            </div>

            {blocos?.length === 0 && (
              <p className="text-sm text-slate-500">Nenhum bloco encontrado nesta conta.</p>
            )}

            {blocos && blocos.length > 0 && (
              <div className="space-y-1">
                {blocos.map(b => (
                  <label key={b.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={escolhidos.has(b.id)}
                      onChange={ev => {
                        const s = new Set(escolhidos)
                        ev.target.checked ? s.add(b.id) : s.delete(b.id)
                        setEscolhidos(s)
                      }}
                    />
                    <span className="flex-1 text-sm font-medium">{b.nome}</span>
                    <span className="text-xs text-slate-500">
                      {b.secoes} seção(ões) · {b.paginas} página(s)
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Ação */}
        {!transferindo && !concluido && (
          <button
            onClick={transferir}
            disabled={!prontoPraTransferir}
            className="w-full py-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-medium
                       disabled:bg-slate-200 disabled:text-slate-400 flex items-center justify-center gap-2"
          >
            Transferir <ArrowRight className="h-4 w-4" />
          </button>
        )}
        {!prontoPraTransferir && !transferindo && !concluido && (
          <p className="text-xs text-slate-500 text-center mt-2">
            Entre com as duas contas e escolha ao menos um bloco.
          </p>
        )}

        {/* Progresso */}
        {(transferindo || concluido) && e && (
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              {transferindo
                ? <><Loader2 className="h-4 w-4 animate-spin text-brand-600" /><span className="font-semibold">Transferindo...</span></>
                : <><CheckCircle2 className="h-4 w-4 text-green-600" /><span className="font-semibold">Concluído</span></>}
            </div>
            {e.blocoAtual && <p className="text-sm text-slate-600 mb-2">Bloco atual: <b>{e.blocoAtual}</b></p>}
            <div className="text-sm space-y-0.5">
              <p>{e.paginasFeitas} página(s) transferida(s)</p>
              {e.paginasPuladas > 0 && <p className="text-slate-500">{e.paginasPuladas} já existiam no destino</p>}
              {e.paginasFalhas > 0 && <p className="text-red-700">{e.paginasFalhas} não puderam ser transferidas</p>}
            </div>
            {/* Falhas nominais: quem não passou aparece com o motivo, em vez de sumir na contagem. */}
            {e.avisos.length > 0 && (
              <details className="mt-3">
                <summary className="text-xs text-slate-600 cursor-pointer">Ver detalhes do que não passou</summary>
                <ul className="mt-2 space-y-0.5 text-xs text-slate-600">
                  {e.avisos.map((a, i) => <li key={i} className="font-mono break-all">{a}</li>)}
                </ul>
              </details>
            )}
            {concluido && (
              <p className="text-xs text-slate-600 mt-3">
                Abra o OneNote com a conta nova. Os blocos aparecem na lista automaticamente.
              </p>
            )}
          </div>
        )}

        {(erro || e?.error) && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800 flex gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{erro || e?.error}</span>
          </div>
        )}

        <p className="text-xs text-slate-400 text-center mt-8">
          Suas credenciais não são vistas nem guardadas por este site — o acesso é feito pela Microsoft.
          O conteúdo não é armazenado: passa direto de uma conta para a outra.
        </p>
      </div>
    </div>
  )
}

function Passo({ numero, titulo, descricao, conectado, upn, desabilitado, onEntrar }: {
  numero: number; titulo: string; descricao: string
  conectado: boolean; upn: string | null; desabilitado: boolean; onEntrar: () => void
}) {
  return (
    <div className={clsx('flex items-center gap-4 p-4 bg-white border rounded-lg',
      conectado ? 'border-green-300' : 'border-slate-200')}>
      <div className={clsx('h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0',
        conectado ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600')}>
        {conectado ? <CheckCircle2 className="h-4 w-4" /> : numero}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">{titulo}</div>
        <div className="text-xs text-slate-500 truncate">{conectado && upn ? upn : descricao}</div>
      </div>
      {!conectado && (
        <button onClick={onEntrar} disabled={desabilitado}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded
                     border border-slate-300 hover:bg-slate-50 disabled:opacity-40 shrink-0">
          <LogIn className="h-3 w-3" /> Entrar
        </button>
      )}
    </div>
  )
}
